import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { IndianRupee, RotateCcw, TrendingUp } from "lucide-react";
import { api } from "../lib/api";

function Tile({ label, value, accent, icon: Icon }) {
  return (
    <div data-testid={`pay-tile-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`} className="bg-white border border-border rounded-2xl p-4">
      <div className="flex items-center gap-3">
        <span className={"h-9 w-9 rounded-xl flex items-center justify-center " + (accent || "bg-accent text-accent-foreground")}>
          <Icon className="h-4 w-4" />
        </span>
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
          <div className="text-xl font-semibold tabular-nums">{value}</div>
        </div>
      </div>
    </div>
  );
}

export default function AdminPayments() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(async () => {
    const p = new URLSearchParams({ page: String(page), page_size: "30" });
    if (status) p.set("status", status);
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    const { data } = await api.get(`/admin/payments?${p.toString()}`);
    setItems(data.items); setSummary(data.summary); setTotal(data.total);
  }, [status, dateFrom, dateTo, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminLayout title="Payments" subtitle={`${total} payment${total === 1 ? "" : "s"}`}>
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
          <Tile label="Successful" value={`₹${summary.total_success_amount.toLocaleString("en-IN")}`} accent="bg-emerald-50 text-emerald-700" icon={IndianRupee} />
          <Tile label="Refunded" value={`₹${summary.total_refunded_amount.toLocaleString("en-IN")}`} accent="bg-rose-50 text-rose-700" icon={RotateCcw} />
          <Tile label="Net" value={`₹${summary.net_amount.toLocaleString("en-IN")}`} accent="bg-primary text-primary-foreground" icon={TrendingUp} />
          <Tile label="Success count" value={summary.success_count} icon={IndianRupee} />
          <Tile label="Refunded count" value={summary.refunded_count} icon={RotateCcw} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Select value={status || "all"} onValueChange={(v) => { setStatus(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger data-testid="pay-status-filter" className="w-44 rounded-full"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="success">Success</SelectItem>
            <SelectItem value="refunded">Refunded</SelectItem>
          </SelectContent>
        </Select>
        <Input data-testid="pay-from" type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className="w-44 rounded-full" />
        <Input data-testid="pay-to" type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className="w-44 rounded-full" />
      </div>

      <div data-testid="admin-payments-table" className="bg-white border border-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="text-left p-3">Payment</th><th className="text-left p-3">Appointment</th>
                <th className="text-left p-3">Patient</th><th className="text-right p-3">Amount</th>
                <th className="text-left p-3">Method</th><th className="text-left p-3">Status</th><th className="text-left p-3">Created</th></tr>
            </thead>
            <tbody>
              {items.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No payments</td></tr>
                : items.map((p) => (
                <tr key={p.id} data-testid={`pay-row-${p.id}`} className="border-t border-border hover:bg-secondary/30">
                  <td className="p-3 font-mono text-xs">{p.id}</td>
                  <td className="p-3"><Link className="text-primary hover:underline font-mono text-xs" to={`/admin/appointments`}>{p.appointment_id}</Link></td>
                  <td className="p-3">{p.patient?.name || p.patient?.phone || "—"}</td>
                  <td className="p-3 text-right tabular-nums">₹{p.amount}</td>
                  <td className="p-3 text-xs">{p.method}</td>
                  <td className="p-3"><span className={"text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full " + (p.status === "success" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700")}>{p.status}</span></td>
                  <td className="p-3 text-xs text-muted-foreground">{new Date(p.created_at).toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {total > 30 && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Page {page} of {Math.ceil(total / 30)}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <Button size="sm" variant="outline" disabled={page >= Math.ceil(total / 30)} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
